import React, { useCallback, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { ContentEditable, Icon, H2, Ellipsis } from '../../atoms';

const EditableTitleContainer = styled.div`
  display: flex;
  align-items: center;
  column-gap: 8px;
`;

const StyledContentEditable = styled(ContentEditable)`
  width: 100%;
  box-sizing: border-box;
  &:focus {
    outline: none;
  }
`;

const ClickableIcon = styled(Icon)`
  cursor: pointer;
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.1s linear;

  ${EditableTitleContainer}:hover &, ${EditableTitleContainer}:focus-within & {
    visibility: visible;
    opacity: 1;
  }
`;

const HeaderTitle = styled(H2)`
  display: flex;
  align-items: center;
  column-gap: 8px;
  padding: 5px;

  ${ClickableIcon} {
    visibility: hidden;
    opacity: 0;
    transition: opacity 0.1s linear;
  }

  &:hover ${ClickableIcon}, &:focus-within ${ClickableIcon} {
    visibility: visible;
    opacity: 1;
  }
`;

const EditableTitle = ({ title, onTitleChange, isReadOnly = false, className = null }) => {
  const [isEditing, setIsEditing] = useState(false);
  const titleRef = useRef(null);
  const ellipsisRef = useRef(null);

  useEffect(() => {
    if (isEditing && titleRef.current) {
      titleRef.current.focus();
      // Move cursor to the end of the text
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(titleRef.current);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }, [isEditing]);

  const handleBlur = useCallback(
    (evt) => {
      setIsEditing(false);
      if (evt.target.innerText.trim() === '') {
        titleRef.current.innerText = title;
      }
      titleRef.current.innerText = evt.target.innerText.trim();
      onTitleChange(titleRef.current.innerText);
    },
    [title, onTitleChange]
  );

  const handleKeyDown = (evt) => {
    if (evt.key === 'Enter') {
      evt.target.blur();
    }
  };

  const renderTitle = useCallback(() => {
    if (isReadOnly) {
      return <Ellipsis ref={ellipsisRef} text={title} limit={100} />;
    }
    if (isEditing) {
      return (
        <>
          <StyledContentEditable
            className={className}
            ref={titleRef}
            $isEditing={isEditing}
            contentEditable={isEditing}
            suppressContentEditableWarning
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
          >
            {title}
          </StyledContentEditable>
          <ClickableIcon
            size={20}
            color='var(--primary-default)'
            name='check'
            onClick={() => setIsEditing(false)}
          />
        </>
      );
    }
    return (
      <>
        <Ellipsis ref={ellipsisRef} text={title} limit={100} />
        <ClickableIcon
          size={20}
          name='pencil'
          color='var(--content-subtitle)'
          onClick={() => setIsEditing(true)}
        />
      </>
    );
  }, [isEditing, isReadOnly, title, setIsEditing, handleBlur, handleKeyDown]);

  return (
    <EditableTitleContainer>
      <HeaderTitle>{renderTitle()}</HeaderTitle>
    </EditableTitleContainer>
  );
};

EditableTitle.propTypes = {
  title: PropTypes.string.isRequired,
  onTitleChange: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool,
  className: PropTypes.string
};

export { EditableTitle };
